import type { ComparatorReasonCode } from './decision-log.js';

export interface StepArtifactQuality {
  kind: 'gap' | 'run_code' | 'structured';
  requiresAgent: boolean;
  selectorAuthor: 'deterministic' | 'agentic' | null;
  predicateRank: number;
  volatileClean: boolean;
  bindingLossless: boolean;
  crossRunScore: { verified: number; attempted: number } | null;
}

export interface CompareStepArtifactOptions {
  incumbentStreak: number;
}

export type StepArtifactVerdict =
  | 'improved'
  | 'not_better'
  | 'protected'
  | 'battery_regression';

export interface StepArtifactComparison {
  verdict: StepArtifactVerdict;
  reason: ComparatorReasonCode;
}

const KIND_RANK: Record<StepArtifactQuality['kind'], number> = {
  gap: 0,
  run_code: 1,
  structured: 2,
};

const AUTHOR_RANK: Record<NonNullable<StepArtifactQuality['selectorAuthor']>, number> = {
  agentic: 1,
  deterministic: 2,
};

const UNKNOWN_PREDICATE_RANK = 900;
const IRREDUCIBLE_PREDICATE_RANK = 1_000;

/**
 * Predicate ladder mirrored from packages/workflow-cache/src/build-locator.ts.
 *
 * buildLocator special-cases test-id/icon/class/volatile-test-id
 * before the predicate array, then iterates the predicate array and tries
 * row-variable, stable-id ancestor, and ancestor scopes for each predicate.
 */
const BASE_PREDICATE_ORDER = [
  'testId',
  'iconClass',
  'semanticClass',
  'volatileTestId+containsText',
  // buildLocator has an inferred placeholder pass before role+name and a
  // normal placeholder pass later; both return the same predicate name.
  'placeholder',
  'role+name',
  'ariaLabel',
  'imgSrc',
  'dataField',
  'role+containsText',
] as const;

const BASE_PREDICATE_RANK = new Map<string, number>(
  BASE_PREDICATE_ORDER.map((predicate, index) => [predicate, index * 10])
);

const SCOPED_SUFFIX_RANK: Record<string, number> = {
  '+row-variable': 1,
  '+stable-id-ancestor': 2,
  '+ancestor': 3,
};

export function derivePredicateRank(predicate: string): number {
  if (predicate === 'indexed-fallback' || predicate === 'none') {
    return IRREDUCIBLE_PREDICATE_RANK;
  }
  if (predicate === 'row-variable+scoped-button-index') {
    return 200;
  }
  if (predicate === 'role+name+clone-index') {
    return 210;
  }

  for (const [suffix, offset] of Object.entries(SCOPED_SUFFIX_RANK)) {
    if (!predicate.endsWith(suffix)) continue;
    const base = predicate.slice(0, -suffix.length);
    const baseRank = BASE_PREDICATE_RANK.get(base);
    return baseRank === undefined ? UNKNOWN_PREDICATE_RANK : baseRank + offset;
  }

  return BASE_PREDICATE_RANK.get(predicate) ?? UNKNOWN_PREDICATE_RANK;
}

function authorRank(author: StepArtifactQuality['selectorAuthor']): number {
  return author ? AUTHOR_RANK[author] : 0;
}

function crossRunRegressed(candidate: StepArtifactQuality, incumbent: StepArtifactQuality): boolean {
  if (!candidate.crossRunScore || !incumbent.crossRunScore) return false;
  if (candidate.crossRunScore.attempted !== incumbent.crossRunScore.attempted) return false;
  return candidate.crossRunScore.verified < incumbent.crossRunScore.verified;
}

function crossRunNoWorse(candidate: StepArtifactQuality, incumbent: StepArtifactQuality): boolean {
  if (!candidate.crossRunScore || !incumbent.crossRunScore) return true;
  if (candidate.crossRunScore.attempted !== incumbent.crossRunScore.attempted) {
    return true;
  }
  return candidate.crossRunScore.verified >= incumbent.crossRunScore.verified;
}

function crossRunStrictlyBetter(
  candidate: StepArtifactQuality,
  incumbent: StepArtifactQuality
): boolean {
  if (!candidate.crossRunScore || !incumbent.crossRunScore) return false;
  if (candidate.crossRunScore.attempted !== incumbent.crossRunScore.attempted) {
    return false;
  }
  return candidate.crossRunScore.verified > incumbent.crossRunScore.verified;
}

export function compareStepArtifacts(
  candidate: StepArtifactQuality,
  incumbent: StepArtifactQuality | null,
  opts: CompareStepArtifactOptions
): StepArtifactComparison {
  if (!incumbent) {
    return { verdict: 'improved', reason: 'CMP_NO_INCUMBENT' };
  }

  if (crossRunRegressed(candidate, incumbent)) {
    return { verdict: 'battery_regression', reason: 'CMP_BATTERY_REGRESSION' };
  }

  const candidateKind = KIND_RANK[candidate.kind];
  const incumbentKind = KIND_RANK[incumbent.kind];
  const gatingNoWorse =
    candidateKind >= incumbentKind &&
    Number(!candidate.requiresAgent) >= Number(!incumbent.requiresAgent) &&
    Number(candidate.volatileClean) >= Number(incumbent.volatileClean) &&
    Number(candidate.bindingLossless) >= Number(incumbent.bindingLossless);

  if (!gatingNoWorse) {
    return { verdict: 'not_better', reason: 'CMP_NOT_BETTER' };
  }

  const gatingStrictlyBetter =
    candidateKind > incumbentKind ||
    Number(!candidate.requiresAgent) > Number(!incumbent.requiresAgent) ||
    Number(candidate.volatileClean) > Number(incumbent.volatileClean) ||
    Number(candidate.bindingLossless) > Number(incumbent.bindingLossless);

  if (gatingStrictlyBetter) {
    return { verdict: 'improved', reason: 'CMP_NOT_BETTER' };
  }

  const tiebreakNoWorse =
    authorRank(candidate.selectorAuthor) >= authorRank(incumbent.selectorAuthor) &&
    candidate.predicateRank <= incumbent.predicateRank &&
    crossRunNoWorse(candidate, incumbent);

  if (!tiebreakNoWorse) {
    return { verdict: 'not_better', reason: 'CMP_NOT_BETTER' };
  }

  const tiebreakStrictlyBetter =
    authorRank(candidate.selectorAuthor) > authorRank(incumbent.selectorAuthor) ||
    candidate.predicateRank < incumbent.predicateRank ||
    crossRunStrictlyBetter(candidate, incumbent);

  if (!tiebreakStrictlyBetter) {
    return { verdict: 'not_better', reason: 'CMP_NOT_BETTER' };
  }

  if (opts.incumbentStreak > 0) {
    return { verdict: 'protected', reason: 'CMP_INCUMBENT_STREAK_PROTECTED' };
  }

  return { verdict: 'improved', reason: 'CMP_NOT_BETTER' };
}
